'use client';

import { useMemo, useOptimistic, useRef, useState } from 'react';
import { proposeTickAction, tickAction } from '@/app/actions';
import {
  assembleContext,
  resolve,
  type ChildContext,
  type Plan,
  type Stimulus,
  type Waiting,
} from '@/domain/resolution';
import { dryRun, offerOf } from '@/domain/act-sheet';
import { holdsSeat, isSeatedParty, type Actor } from '@/domain/seats';
import { domainError, errorMessage, type WorkItemState } from '@/domain/types';
import { m, type Message } from '@/i18n';
import { useT } from '@/lib/locale-context';
import type { ListRow } from '@/read/list';
import {
  expectedAtoms,
  mintKey,
  runOrQueue,
  usePendingAtoms,
  type PendingAtoms,
} from '@/components/live-queue';
import { clearJustTicked } from '@/components/live-store';
import { requestRefresh, useWriteTransition } from '@/lib/refresh-gate';

/**
 * Completion on a checklist surface (docs/CHECKLIST.md §3, RESOLUTION.md §2,
 * MOBILE.md §8): what a tick does to the lines around it. Owned by the surface
 * (the lens checklist rail, the checklist view) rather than the line, because a
 * ticked line has to keep its place in the surface's own order.
 *
 * **A tick says "I am done", and that statement is always true**
 * (docs/RESOLUTION.md §2, settled 2026-09-07, built 2026-09-21). There is no
 * table of tick acts here any more: the surface serializes the STIMULUS
 * (`{ kind: 'tick', itemId, surface, key, deviceTime }`) and one server action
 * resolves what it means against the truth at act time. What the person must be
 * able to see is exactly one thing - what the item still WAITS on - and that
 * comes back with the plan as its trailing note. Ticked means "I am done", gone
 * means "everyone is done".
 *
 * Four layers over the rows the server rendered:
 *
 * 1. **Pending** (`useOptimistic` while the act is in flight, and the offline
 *    QUEUE's overlay for as long as a gesture is deferred): the line reads
 *    ticked at the tap; the server's answer or a refusal replaces it (§8).
 *    Pending is a READING, never the row: the row's own atoms - what
 *    `data-closed` renders - never take done-ness before the server has
 *    answered (docs/SYNC.md §7.3). They LOSE it at the tap of an un-tick,
 *    which is the other half of the same rule: the two layers may never say
 *    opposite things about one line (2026-09-18).
 * 2. **Just ticked** (kept until the surface unmounts or its filter changes):
 *    closed work is hidden by default (docs/VIEWS.md §3a), so a refreshed set
 *    no longer carries a completed line - and a line that vanishes under the
 *    thumb cannot be taken back. Every line completed HERE is snapshotted at
 *    the tap and stood back in, closed, at its old position whenever the
 *    server rows drop it. A snapshot never overrides a row the server still
 *    sends (another device's reopen shows through), and a refused tick never
 *    stays in place.
 * 3. **Notes**: what the ticked line still waits on, from the plan the client
 *    resolved at the tap and then from the one the server executed.
 * 4. **Notices**: a refused act's typed message, spoken on the line.
 *
 * **The strike waits on nothing** (§10). The resolver is pure, so the client
 * runs it on the row it already holds - the atoms, the seats, the weights
 * (`openChildren` / `openBlockers`) and the review duty all ride the row - and
 * the line is ticked before the request leaves the phone. The server is
 * reconciliation, not display: its answer carries the row's new truth, which
 * this patches in place instead of refreshing the whole lens (§10: "the real
 * cost today is the refresh"). The full refresh rides the SSE ping.
 *
 * **Every layer covers the whole PLAN, never the tapped row alone**
 * (2026-09-22). A tick sweeps the open sub-tasks that are the person's
 * alone (§6; an owner's since 2026-09-22, a doer's since 2026-09-27), so the
 * dry run is handed the subtree the surface itself holds
 * (`surfaceSubtree`) and the plan then names every item it closes
 * (`plannedIds`). Those ids take the pending reading, the snapshot and the
 * note together, and the server's answer - which carries a ROW PER ITEM -
 * settles them together (`settleSweep`). An un-tick inverts the same set,
 * remembered from the tick that minted it, because the reversal reaches every
 * item the origin plan touched (§8). One rule for one line is the same rule
 * for the set.
 *
 * The surface sees one fact the server sees and it does not: an OPEN item
 * outside the subtree that depends on a line (`outsideDependents`). The rail's
 * rows carry no such edge, so the dry run reads it as zero and may strike a
 * line the safe preset then leaves; the answer retires that claim on arrival,
 * exactly as it retires a refused one. The other tie - a line another open
 * list holds - the rows do carry (`ListRow.parents`), so the common case is
 * right at the tap.
 *
 * The just-ticked set is scoped to the FILTER it was minted under (`scope`): a
 * changed criteria set is a different view, and snapshots from the old one
 * would sit in it unfiltered. The closed flag is deliberately NOT part of the
 * scope (2026-09-12): revealing or hiding older closed work is not an arrival,
 * and stamping it made the set vanish on "Show closed" and RESURRECT on the
 * next "Hide closed", so a line ticked in this sitting hopped between its seat
 * and the fold with every press. Under one scope it simply keeps its seat.
 *
 * **The tidy ends the sitting on demand** (docs/CHECKLIST.md §3.1, Leon
 * 2026-09-27). The next arrival (a reload, a navigation back) folds what was
 * ticked here, and the installed app has no reload, so `tidy` does the same at
 * the person's request: the own just-ticked set and the live store's remote
 * closures go, and a refresh brings the fold's count. The button and the pull
 * that call it exist only while `standing` says a line would move.
 */

/** The seats a row stands for, in the shape the domain reads them. */
function seatsOf(row: ListRow): { requesters: string[]; contributors: string[] } {
  return { requesters: row.requester ? [row.requester] : [], contributors: row.contributors };
}

/**
 * **A line the viewer may only VIEW** (docs/CHECKLIST.md §3.1): no tick, a lock
 * where the box would be. It is step 5's lane entry point (§1: a missing right
 * is a lane, not a dead end) and the same predicate the spine gates on
 * (`mayAct`, resolved into the row's `canAct` by the read layer). A seated
 * party always reaches their own contract, so a seat is never locked out.
 * Pure - exported for its test.
 */
export function lockedFor(actor: Actor, row: ListRow): boolean {
  return !row.canAct && !isSeatedParty(actor, seatsOf(row));
}

/**
 * **Whether a row reads TICKED for this viewer.** A tick is the forward edge of
 * the row's position for the viewer (docs/RESOLUTION.md §2, 2026-11-03), so a
 * row reads ticked once that edge is behind them: a concluded row is done with
 * either way (finished, or cancelled - the ✕ reading of docs/CLOSED_AXIS.md),
 * and a row in REVIEW is ticked for everyone but its reviewer, whose tick there
 * is still ahead (Accept). The reviewer sees the box empty with the quiet
 * "in review" note (`inReviewFor`). The pending layer overrides it while an act
 * is in flight. Pure.
 */
export function tickedOf(row: ListRow, actor: Actor): boolean {
  return row.closed || (row.delivered && !inReviewFor(actor, row));
}

/**
 * **A row in Review whose review is this viewer's** (the requester seat, the
 * reviewer of the edge table; a row carries no flow gatekeepers): its box is
 * empty, its tick is Accept, and the row says "in review" quietly. Pure.
 */
export function inReviewFor(actor: Actor, row: ListRow): boolean {
  return row.delivered && !row.closed && holdsSeat(actor, seatsOf(row).requesters);
}

export interface Completion {
  /** id -> ticked as the viewer just set it, while the act is in flight. */
  pending: ReadonlyMap<string, boolean>;
  /** id -> what the ticked line still waits on (§2's trailing note). */
  notes: ReadonlyMap<string, Waiting>;
  /** id -> a refused act's message, in the page's locale (`useCompletion`
   *  formats the descriptors it holds). */
  notices: ReadonlyMap<string, string>;
  /** id -> who a tick on this line was ASKED of instead (docs/RESOLUTION.md
   *  §12 step 8): a locked line's tap, or a tick the server filed as a
   *  proposal. The line reads "asked <name>" for the rest of the sitting. */
  asked: ReadonlyMap<string, readonly string[]>;
  /**
   * Ids TICKED on this surface in this sitting. Two jobs, one set:
   *  - a row the server then drops is stood back in at its old index, so
   *    finishing something does not make it vanish under you;
   *  - a row the server still sends KEEPS ITS PLACE in the open lines instead
   *    of sinking into the done fold (the rail's `inPlace`, Leon 2026-09-12).
   * Either way the line reads struck and done; the fold collects it on the
   * next arrival.
   */
  justTicked: ReadonlySet<string>;
  /** A line in `justTicked` still reads ticked: what a tidy would fold. False
   *  when every line ticked here was un-ticked again, so the tidy is offered
   *  only while it would move something (DESIGN_SYSTEM.md, "push, don't park"). */
  standing: boolean;
  /** Fold the lines ticked in this sitting now, as the next arrival would
   *  (docs/CHECKLIST.md §3.1, Leon 2026-09-27): the Tidy button and the pull
   *  on compact both call it. */
  tidy: () => void;
  toggle: (row: ListRow) => void;
  /** The lock as the entry (docs/CHECKLIST.md §7): a line the viewer may only
   *  view asks its owners to take the tick. */
  ask: (row: ListRow) => void;
}

/** A completed row's snapshot and the position it held in the shown list. */
export interface JustTickedRow {
  row: ListRow;
  index: number;
}

/**
 * The sorted rows with every just-ticked row the server dropped stood back in
 * at its old index (clamped to the end); rows the server still sends win.
 * Pure - exported for its test.
 */
export function withJustTicked(
  sorted: ListRow[],
  justTicked: ReadonlyMap<string, JustTickedRow>,
): ListRow[] {
  if (justTicked.size === 0) return sorted;
  const present = new Set(sorted.map((r) => r.id));
  const missing = [...justTicked.values()]
    .filter((l) => !present.has(l.row.id))
    .sort((a, b) => a.index - b.index);
  if (missing.length === 0) return sorted;
  const out = [...sorted];
  for (const l of missing) out.splice(Math.min(l.index, out.length), 0, l.row);
  return out;
}

/**
 * Each row as last seen before this sitting ticked it (docs/CHECKLIST.md
 * §3.1): a line ticked here keeps its SECTION as well as its seat. Grouped by
 * an atom, the tick's answer would carry a line from "Conclusion: Open" to
 * "Conclusion: Closed", so a grouping reads a just-ticked line as this map
 * holds it (`asSeenBeforeTick`). Adopted at render, and only when a row object
 * changed, so it converges. The checklist's two mounts share it.
 */
export function useSeenBeforeTick(
  rows: readonly ListRow[],
  justTicked: ReadonlySet<string>,
): ReadonlyMap<string, ListRow> {
  const [seen, setSeen] = useState<ReadonlyMap<string, ListRow>>(
    () => new Map(rows.map((r) => [r.id, r])),
  );
  if (rows.some((r) => !justTicked.has(r.id) && seen.get(r.id) !== r)) {
    const next = new Map(seen);
    for (const r of rows) if (!justTicked.has(r.id)) next.set(r.id, r);
    setSeen(next);
  }
  return seen;
}

/** The rows a grouping reads: each just-ticked row as it was last seen before
 *  its tick (`useSeenBeforeTick`), every other row as it is. Pure. */
export function asSeenBeforeTick(
  rows: readonly ListRow[],
  seen: ReadonlyMap<string, ListRow>,
  justTicked: ReadonlySet<string>,
): ListRow[] {
  return rows.map((r) => (justTicked.has(r.id) ? (seen.get(r.id) ?? r) : r));
}

/**
 * The nesting a just-ticked row had when it was last seen. A line ticked inside a
 * section is closed, so with closed work hidden the next server render drops the
 * row AND its `under` entry; `withJustTicked` stands the row back in, and without
 * its parent it drew at the top of the rail with its section reading one child
 * short (review 2026-09-12). This stands the parents back in with the row: for
 * every just-ticked row the server dropped, the parents `remembered` from an
 * earlier render. Returns `under` itself when nothing changes. Pure.
 */
export function withJustTickedUnder(
  under: Readonly<Record<string, readonly string[]>>,
  remembered: Readonly<Record<string, readonly string[]>>,
  justTicked: ReadonlySet<string>,
  serverIds: ReadonlySet<string>,
): Readonly<Record<string, readonly string[]>> {
  let out: Record<string, readonly string[]> | null = null;
  for (const id of justTicked) {
    const parents = remembered[id];
    if (serverIds.has(id) || under[id] !== undefined || parents === undefined) continue;
    out ??= { ...under };
    out[id] = parents;
  }
  return out ?? under;
}

/**
 * Nest the rows the live store APPENDED (a line another person added, pushed as
 * an id `liveRows` carries but the server props - `propIds` - did not) under
 * their parent, so they land in the right checklist section instead of at the
 * top (docs/SYNC.md §7.3). `under` maps a row to its PARENT ids; a pushed add
 * whose parent is on the list gets `[parentId]`, and then tails that section as
 * an unranked, newest line. A top-level add (no parent on the list) gets no
 * entry and tails the top group. Server/just-ticked `under` entries win. Returns
 * `under` itself when nothing changes. Pure.
 */
export function nestPushedAdds(
  under: Readonly<Record<string, readonly string[]>>,
  liveRows: ListRow[],
  propIds: ReadonlySet<string>,
): Readonly<Record<string, readonly string[]>> {
  const present = new Set(liveRows.map((r) => r.id));
  let out: Record<string, readonly string[]> | null = null;
  for (const r of liveRows) {
    if (propIds.has(r.id) || under[r.id] !== undefined) continue;
    const parentId = r.parent?.id;
    if (!parentId || !present.has(parentId)) continue;
    out ??= { ...under };
    out[r.id] = [parentId];
  }
  return out ?? under;
}

/**
 * How many closed rows the "Show N closed" footer should count: the server's
 * hidden count MINUS every just-ticked row the server dropped and this surface
 * stood back in. A line ticked here is standing right there, struck; counting
 * it as hidden made the footer say "Show 1 closed" about a line in plain view,
 * and pressing it then re-seated that line (Leon, 2026-09-12). Pure.
 */
export function hiddenClosedCount(
  closedHidden: number,
  serverIds: ReadonlySet<string>,
  justTicked: ReadonlySet<string>,
): number {
  let restored = 0;
  for (const id of justTicked) if (!serverIds.has(id)) restored++;
  return Math.max(0, closedHidden - restored);
}

/**
 * Whether a line ticked here still STANDS, reading done in its seat: what a tidy
 * would fold (docs/CHECKLIST.md §3.1). A line ticked and then un-ticked stays in
 * the just-ticked set (its snapshot holds its atoms in case the un-tick is
 * refused), so the set's size alone would offer a tidy that moves nothing. The
 * reading is the rail's own: the in-flight tick, else the row's atoms. Pure.
 */
export function standingOf(
  rows: readonly ListRow[],
  justTicked: ReadonlySet<string>,
  pending: ReadonlyMap<string, boolean>,
  actor: Actor,
): boolean {
  if (justTicked.size === 0) return false;
  return rows.some((r) => justTicked.has(r.id) && (pending.get(r.id) ?? tickedOf(r, actor)));
}

/**
 * The `WorkItemState` a checklist ROW stands for: its atoms, its verdict, its
 * seats and its title, which is everything the tick's rows of the resolver's
 * table read. The fields a row does not carry are inert here and are never
 * read: step 5 runs off the row's RESOLVED standing (`access.standing`, the
 * read layer's own grade) rather than off `visibility`, and no tick command
 * touches the description, the criteria or the note. The server re-resolves
 * against the real item inside the transaction either way (§1 step 7). Pure.
 */
export function rowState(row: ListRow): WorkItemState {
  const seats = seatsOf(row);
  return {
    verdict: row.verdict,
    requesters: seats.requesters,
    contributors: seats.contributors,
    delivered: row.delivered,
    active: row.active,
    closed: row.closed,
    priority: row.priority,
    acceptanceCriteria: null,
    description: null,
    note: null,
    visibility: 'protected',
    inheritsAccess: true,
    title: row.title,
  };
}

/**
 * **The optimistic reading** (§9's dry run, §10's "the strike waits on
 * nothing"): the same pure resolver the server runs, over the row the surface
 * already holds. It gives the line its trailing note and the queue its expected
 * atoms before the request leaves the device. Only a TICK previews - an un-tick
 * is the inverse of a plan found through the log's origin marker (§2), which no
 * row carries, so its reading is simply "not ticked any more". Pure.
 */
export function previewTick(
  actor: Actor,
  row: ListRow,
  stimulus: Stimulus,
  /** The open subtree the surface holds (`surfaceSubtree`), so the dry run
   *  plans the same sweep the server will (§6). Empty = no subtree gathered,
   *  and the tick closes the line alone, as it did before. */
  children: readonly ChildContext[] = [],
): Plan | null {
  const item = rowState(row);
  const context = assembleContext({
    item,
    actor,
    access: {
      standing: row.canAct ? 'act' : 'view',
      ownerReach: holdsSeat(actor, item.requesters),
    },
    weight: { openChildren: row.openChildren, openBlockers: row.openBlockers },
    reviewRequired: row.reviewRequired,
    mode: 'interactive',
    ...(children.length > 0 ? { children } : {}),
    // Flow structure is not on a row; the server's own context carries it and
    // adds the position change to the plan (§6, flows ride the plan).
    flows: [],
  });
  const resolution = resolve(stimulus, context);
  return resolution.kind === 'plan' ? resolution.plan : null;
}

/**
 * **Whether "Make this a line" is offered on a sub-item row**
 * (docs/CHECKLIST.md §2): the row holds nothing but its title (`bare`, the
 * read's `isBareChild`), and the act sheet's own dry run of Cancel on it - the
 * close the demotion takes - is takeable by this viewer from here, with no
 * lane and no question. Never a hand-folded seat check: the same pure table
 * the server runs decides it. Pure.
 */
export function demotionOffered(actor: Actor, row: ListRow): boolean {
  if (row.bare !== true || row.closed) return false;
  const item = rowState(row);
  const context = assembleContext({
    item,
    actor,
    access: {
      standing: row.canAct ? 'act' : 'view',
      ownerReach: holdsSeat(actor, item.requesters),
    },
    weight: { openChildren: row.openChildren, openBlockers: row.openBlockers },
    reviewRequired: row.reviewRequired,
    mode: 'interactive',
    flows: [],
  });
  const offer = offerOf(dryRun({ kind: 'cancel' }, row.id, context));
  return offer.enabled && offer.lane === undefined && offer.question === undefined;
}

/**
 * **The open subtree of one line, read off the rows the surface already
 * holds** (§6's sweep, §10's dry run). A checklist rail IS the subtree: every
 * row carries its visible parents (`ListRow.parents`), so the descendants of
 * the tapped line, their depth and their seats need no gather at all - which
 * is what lets the swept lines strike in the same frame as the one that was
 * tapped.
 *
 * It mirrors the server's own gather (`assembleOpenSubtree`,
 * `src/read/resolution-context.ts`) fact for fact, with TWO exceptions it
 * cannot see: an open item OUTSIDE the subtree that depends on a line
 * (`outsideDependents`) is a `blocks` edge, and no row carries one; and a
 * parent inside the subtree that the viewer sees only as its stub
 * (`stubParentsInSubtree`) is no row of the surface, since a row carries only
 * the parents its viewer can open. Each reads as empty here and the server's
 * answer corrects it, the same way `ListRow` already carries
 * `reviewRequired: false` on a projection that has no flow means. The other
 * tie - an open parent outside the subtree - the rows do
 * carry, and a parent whose row this surface holds and knows to be CLOSED is
 * no tie at all (the server's tie query joins on `closed = false`).
 *
 * Depth walks THROUGH a closed item exactly as the server's recursive CTE
 * does, and only OPEN rows become children. Pure.
 */
export function surfaceSubtree(rows: readonly ListRow[], targetId: string): ChildContext[] {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const kids = new Map<string, ListRow[]>();
  for (const row of rows) {
    for (const parent of row.parents) {
      const held = kids.get(parent.id);
      if (held) held.push(row);
      else kids.set(parent.id, [row]);
    }
  }
  // Breadth first, so each id keeps its SHALLOWEST depth (the CTE's
  // `min(depth)`); the bound matches the CTE's own.
  const depth = new Map<string, number>();
  let frontier = [targetId];
  for (let d = 1; frontier.length > 0 && d <= 64; d += 1) {
    const next: string[] = [];
    for (const parentId of frontier) {
      for (const kid of kids.get(parentId) ?? []) {
        if (kid.id === targetId || depth.has(kid.id)) continue;
        depth.set(kid.id, d);
        next.push(kid.id);
      }
    }
    frontier = next;
  }
  const out: ChildContext[] = [];
  for (const [itemId, d] of depth) {
    const row = byId.get(itemId);
    if (!row || row.closed) continue;
    const parentsInSubtree: string[] = [];
    let outsideParents = 0;
    for (const parent of row.parents) {
      const parentRow = byId.get(parent.id);
      // A parent this surface holds and knows to be closed ties nothing.
      if (parentRow?.closed === true) continue;
      if (parent.id === targetId || depth.has(parent.id)) parentsInSubtree.push(parent.id);
      else outsideParents += 1;
    }
    out.push({
      itemId,
      depth: d,
      state: rowState(row),
      canAct: row.canAct,
      outsideParents,
      outsideDependents: 0,
      parentsInSubtree,
      stubParentsInSubtree: [],
    });
  }
  // Deepest first, as the sweep concludes them.
  return out.sort((a, b) => b.depth - a.depth);
}

/**
 * **Every item a plan touches, the tapped one first**: the ids its optimistic
 * layer covers. The target leads whether or not the plan reached it (a plan
 * refused at the dry run still claims the line that was tapped, so the reading
 * and the refusal land on the same row). Pure.
 */
export function plannedIds(targetId: string, plan: Plan | null): string[] {
  const out = [targetId];
  const seen = new Set(out);
  for (const step of plan?.steps ?? []) {
    if (seen.has(step.itemId)) continue;
    seen.add(step.itemId);
    out.push(step.itemId);
  }
  return out;
}

/**
 * What the plan expects of the atoms of every item BESIDES the tapped one, for
 * the offline queue's overlay (docs/SYNC.md §7.3, §7.4): a deferred gesture
 * shows its whole reading while it waits, not only the line that was tapped.
 * Pure.
 */
export function sweptExpectation(
  targetId: string,
  plan: Plan | null,
): Record<string, PendingAtoms> {
  const out: Record<string, PendingAtoms> = {};
  for (const step of plan?.steps ?? []) {
    if (step.itemId === targetId) continue;
    out[step.itemId] = expectedAtoms([step.command], out[step.itemId] ?? {});
  }
  return out;
}

/**
 * The rows with every patched row standing in for the one the server sent.
 * **A patch stands only while it is no OLDER than the row the server sends**:
 * an arrival that still says open is behind the act and the patch holds; one
 * that already carries the act (or a later one) wins and retires it, so this
 * can never hold a line in the past (docs/SYNC.md §7.3). Pure.
 */
export function withPatched(rows: ListRow[], patched: ReadonlyMap<string, ListRow>): ListRow[] {
  if (patched.size === 0) return rows;
  return rows.map((r) => {
    const p = patched.get(r.id);
    return p && p.updatedAt.getTime() >= r.updatedAt.getTime() ? p : r;
  });
}

/** What the server's answer does to a gesture's optimistic claim. */
export interface Settlement {
  /** Rows the record now reads as the gesture said: patch them in place, and
   *  keep the ones the surface holds a seat for. */
  took: ListRow[];
  /** Claimed ids the record did NOT take - no row came back, or the row reads
   *  the other way (a tie the safe sweep left, a refusal). The snapshot goes
   *  and the reading with it. */
  retired: string[];
  /** A refusal's message, on the line that was TAPPED: one gesture, one
   *  notice, never one per swept line. */
  notices: Map<string, Message>;
}

/**
 * **The reconciliation, for the whole set** (§10: the server is
 * reconciliation, not display). The claim is what the dry run struck at the
 * tap; the answer is a row per item the plan actually touched. A row reading
 * as the gesture said is taken; a claimed id the answer does not confirm is
 * retired, which is the one correction the surface's own subtree can need (it
 * cannot see a `blocks` edge into the set). A refusal retires the whole claim
 * and speaks once. Pure.
 */
export function settleSweep(
  claimed: readonly string[],
  targetId: string,
  answer: { ok: true; rows: readonly ListRow[] } | { ok: false; message: Message },
  /** The reading the gesture claimed: ticked for a tick, un-ticked for an
   *  un-tick. */
  ticked: boolean,
  /** Who ticked: a row in review reads ticked for everyone but its reviewer. */
  actor: Actor,
): Settlement {
  if (!answer.ok) {
    return { took: [], retired: [...claimed], notices: new Map([[targetId, answer.message]]) };
  }
  const answered = new Map(answer.rows.map((r) => [r.id, r]));
  return {
    took: answer.rows.filter((r) => tickedOf(r, actor) === ticked),
    retired: claimed.filter((id) => {
      const fresh = answered.get(id);
      return !fresh || tickedOf(fresh, actor) !== ticked;
    }),
    notices: new Map(),
  };
}

/**
 * **What a ticked line still waits on, read from the row itself.** The note a
 * tick produces is the plan's (`Completion.notes`), and that lives as long as
 * the sitting; this is the same sentence derived from the row, so a line that
 * was ticked yesterday still says what it is waiting for. It describes a
 * DELIVERED AND OPEN row, whatever act delivered it: the owner's own line waits
 * on an authored review, anyone else's on the requester's close. Since
 * 2026-09-27 a tick leaves such a row only where a review duty holds the close
 * (a doer's tick closes otherwise, docs/RESOLUTION.md §2), so the requester case
 * is a row delivered through the item page's Deliver act, or ticked by a doer
 * before that ruling. It is a pure function of the row and who is looking.
 * Pure.
 *
 * **Open sub-tasks are not a wait** (Leon, 2026-09-22): an owner's tick closes,
 * and the children carry on as their own work. The case went with the deferred
 * close it belonged to.
 */
export function waitsOf(actor: Actor, row: ListRow): Waiting | undefined {
  // Only a row in Review waits, and only on someone else's review: the
  // reviewer's own row has its Accept ahead (`inReviewFor`).
  if (row.closed || !row.delivered || inReviewFor(actor, row)) return undefined;
  const { requesters } = seatsOf(row);
  return requesters.length > 0 ? { on: 'requester', refs: requesters } : { on: 'review' };
}

const NO_PENDING: ReadonlyMap<string, boolean> = new Map();
const NO_NOTICES: ReadonlyMap<string, Message> = new Map();
const NO_NOTICE_TEXT: ReadonlyMap<string, string> = new Map();
const NO_NOTES: ReadonlyMap<string, Waiting> = new Map();
const NO_ASKED: ReadonlyMap<string, readonly string[]> = new Map();
const NO_JUST_TICKED: ReadonlyMap<string, JustTickedRow> = new Map();
const NO_PATCHED: ReadonlyMap<string, ListRow> = new Map();
const NO_IDS: ReadonlySet<string> = new Set();
const NO_ATOMS = { closed: false, delivered: false };

export function useCompletion(
  sorted: ListRow[],
  scope: string,
  /** Who is ticking, with the groups they speak for: the resolver's actor. */
  actor: Actor,
  /** The just-ticked rule's REMOTE closures (docs/SYNC.md §7.3, from the live
   *  store): rows someone else closed while they were on this surface open,
   *  unioned with the viewer's own just-ticked lines so they render identically. */
  remoteJustTicked: ReadonlyMap<string, JustTickedRow> = NO_JUST_TICKED,
  /** What the offline queue needs when a tap cannot reach the server
   *  (docs/SYNC.md §7.4): which surface it was taken on, for the cursor the
   *  deferred gesture is based on. Absent = the act still queues, without it. */
  offline: { surfaceKey?: string } = {},
): { rows: ListRow[]; completion: Completion } {
  const [, startAct] = useWriteTransition();
  const t = useT();
  // One gesture sets the reading for EVERY item its plan covers (§6's sweep),
  // so the reducer takes the set rather than a row.
  const [inFlight, setPending] = useOptimistic<
    ReadonlyMap<string, boolean>,
    { ids: readonly string[]; ticked: boolean | null }
  >(NO_PENDING, (m, p) => {
    const next = new Map(m);
    // `null` takes the reading back rather than flipping it: a claim the answer
    // retired says nothing about the line, so it falls back to the row's own
    // atoms - which for a delivered line is still ticked.
    for (const id of p.ids) {
      if (p.ticked === null) next.delete(id);
      else next.set(id, p.ticked);
    }
    return next;
  });
  // The deferred gestures' expected reading (docs/SYNC.md §7.3): an act taken
  // with no network reads ticked for as long as it waits in the queue, through
  // the SAME pending layer an in-flight act uses - the row's atoms are
  // untouched. An act in flight right now wins over one that is merely queued.
  const queued = usePendingAtoms();
  const pending = useMemo(() => {
    if (queued.size === 0) return inFlight;
    const m = new Map<string, boolean>();
    for (const [id, atoms] of queued) {
      const ticked = atoms.closed ?? atoms.delivered;
      if (ticked !== undefined) m.set(id, ticked);
    }
    for (const [id, ticked] of inFlight) m.set(id, ticked);
    return m;
  }, [queued, inFlight]);
  const [notices, setNotices] = useState(NO_NOTICES);
  const [notes, setNotes] = useState(NO_NOTES);
  const [asked, setAsked] = useState(NO_ASKED);
  // The rows the server answered a tick with (§10: the action returns the row's
  // new truth and the client patches it, rather than refreshing the lens).
  const [patched, setPatched] = useState(NO_PATCHED);
  // The just-ticked rows, stamped with the scope they were minted under; any
  // other scope reads them as empty (derived, so no effect has to clear them).
  const [held, setHeld] = useState<{ scope: string; rows: ReadonlyMap<string, JustTickedRow> }>({
    scope,
    rows: NO_JUST_TICKED,
  });
  const gestures = useRef(0);
  // Per tapped line, the set its tick claimed (§6's sweep), so the un-tick
  // inverts exactly what the tick struck. The plan's reversal is found through
  // the origin marker on the SERVER (§8); no row carries it, so this sitting's
  // own memory is what lets the un-tick be optimistic at all.
  const swept = useRef(new Map<string, string[]>());
  // Counts the tidies of this sitting. A tick in flight when the person tidies
  // must not seat its line again when its answer lands afterwards.
  const tidies = useRef(0);
  const ownJustTicked = held.scope === scope ? held.rows : NO_JUST_TICKED;
  // The viewer's own just-ticked lines, unioned with the just-ticked rule's REMOTE
  // closures: both keep a struck line in its seat, and both feed the
  // stand-back-in (`rows`) and the in-place set (`justTicked`). An id in both -
  // the viewer's own tick, also pushed back - appears once, the local snapshot
  // winning (it holds the index the viewer saw).
  const merged = useMemo(() => {
    if (remoteJustTicked.size === 0) return ownJustTicked;
    if (ownJustTicked.size === 0) return remoteJustTicked;
    const m = new Map(remoteJustTicked);
    for (const [id, row] of ownJustTicked) m.set(id, row);
    return m;
  }, [ownJustTicked, remoteJustTicked]);

  const rows = useMemo(
    () => withPatched(withJustTicked(sorted, merged), patched),
    [sorted, merged, patched],
  );
  const justTicked = useMemo(() => (merged.size === 0 ? NO_IDS : new Set(merged.keys())), [merged]);
  const standing = useMemo(
    () => standingOf(rows, justTicked, pending, actor),
    [rows, justTicked, pending, actor],
  );

  function tidy() {
    tidies.current += 1;
    setHeld({ scope, rows: NO_JUST_TICKED });
    if (offline.surfaceKey) clearJustTicked(offline.surfaceKey, justTicked);
    // A fresh read, as on an arrival: the server hides what was closed and
    // counts it in the fold's "Show N closed". Nothing was written, so this is
    // a request to the refresh coordinator, not an action's answer.
    requestRefresh('checklist-tidy');
  }

  function editOwnJustTicked(edit: (m: Map<string, JustTickedRow>) => void) {
    setHeld((h) => {
      const next = new Map(h.scope === scope ? h.rows : NO_JUST_TICKED);
      edit(next);
      return { scope, rows: next };
    });
  }

  /** Rewrite a just-ticked snapshot's atoms in place, keeping its index; a no-op when
   *  nothing is held for that id. */
  function setKeptAtoms(id: string, atoms: { closed: boolean; delivered: boolean }) {
    editOwnJustTicked((m) => {
      const held = m.get(id);
      if (held) m.set(id, { ...held, row: { ...held.row, ...atoms } });
    });
  }

  function setNote(id: string, waits: Waiting | undefined) {
    setNotes((m) => {
      if (!waits) {
        if (!m.has(id)) return m;
        const n = new Map(m);
        n.delete(id);
        return n;
      }
      return new Map(m).set(id, waits);
    });
  }

  function toggle(row: ListRow) {
    const ticked = pending.get(row.id) ?? tickedOf(row, actor);
    const kind = ticked ? 'untick' : 'tick';
    // The device's own key for this gesture (docs/MOBILE.md §8): one gesture,
    // one key, whether it goes straight to the server or waits in the queue.
    const key = mintKey(gestures.current++, Date.now(), Math.random());
    const deviceTime = new Date().toISOString();
    const tidiesAtTap = tidies.current;
    const stimulus: Stimulus = {
      kind,
      itemId: row.id,
      key,
      source: 'person',
      surface: 'checklist',
      deviceTime,
    };
    // The optimistic reading, from the same pure table the server runs, over
    // the subtree this surface already holds - so the lines a tick SWEEPS
    // strike in the same frame as the line that was tapped (§6, §10).
    const plan =
      kind === 'tick' ? previewTick(actor, row, stimulus, surfaceSubtree(rows, row.id)) : null;
    const expected = plan
      ? expectedAtoms(plan.steps.filter((s) => s.itemId === row.id).map((s) => s.command))
      : { delivered: false, closed: false };
    // Every item this gesture covers. A tick reads it off its own plan; an
    // un-tick inverts the WHOLE plan through the origin marker (§8), and the
    // set the tick claimed is what this sitting remembers of it - after a
    // reload there is nothing to remember, and the answer's rows strike the
    // rest a beat late.
    const claimed =
      kind === 'tick' ? plannedIds(row.id, plan) : (swept.current.get(row.id) ?? [row.id]);
    // The atoms each claimed line reads right now, so a refused un-tick can put
    // them back exactly as the tap found them.
    const before = new Map(
      claimed.map((id) => {
        const r = rows.find((x) => x.id === id);
        return [id, { closed: r?.closed ?? false, delivered: r?.delivered ?? false }];
      }),
    );

    // Held from the tap, outside the transition, so the snapshot is already
    // committed when the refreshed rows land (with or without the row).
    // EVERY tick is recorded, not only a concluding one: the set is also
    // what keeps a ticked line in its place (see `justTicked`), and a delivery
    // reads done without closing anything.
    //
    // The snapshot holds the row AS THE SERVER LAST SENT IT (2026-09-14): the
    // atoms it carries are what `data-closed` renders, so fabricating `closed`
    // at the tap made a line read concluded before anything was written - and
    // a stale snapshot from an earlier tick then satisfied that reading for a
    // later one (the ~50% flake in e2e/checklist.spec.ts). The act's own
    // reading is the pending layer; the atoms follow when the server answers.
    //
    // TAKING done-ness therefore waits for the server; CLEARING it does not
    // (2026-09-18). An un-tick blanks the snapshot's atoms here, at the tap, in the
    // same urgent update as the tick's own snapshot, because the two layers
    // must never say opposite things: doing it at settle instead left the
    // snapshot reading `closed: true` under a line the pending layer already
    // showed un-ticked, and that rewrite reached the DOM a round trip late or
    // not at all. Restored below if the un-tick is refused.
    if (kind === 'tick') {
      editOwnJustTicked((m) => {
        for (const id of claimed) {
          const at = rows.findIndex((r) => r.id === id);
          const held = at >= 0 ? rows[at] : id === row.id ? row : undefined;
          if (held) m.set(id, { row: held, index: at < 0 ? rows.length : at });
        }
      });
      swept.current.set(row.id, claimed);
    } else {
      for (const id of claimed) setKeptAtoms(id, { closed: false, delivered: false });
      swept.current.delete(row.id);
    }
    // What the line waits on, said at the tap and corrected by the server. The
    // note is the TAPPED line's: a swept line is concluded and waits on nobody.
    setNote(row.id, kind === 'tick' ? plan?.waits : undefined);

    startAct(async () => {
      setPending({ ids: claimed, ticked: kind === 'tick' });
      setNotices((m) => {
        const n = new Map(m);
        n.delete(row.id);
        return n;
      });
      // Offline, or when the request cannot leave the device, the GESTURE is
      // deferred instead of refused (docs/SYNC.md §7.4, MOBILE.md §8): the
      // queue holds the stimulus, the store already shows the expected result,
      // and the reconnect replays it through the same pipeline - where its
      // meaning is computed again, against the truth at arrival.
      const res = await runOrQueue(
        {
          workItemId: row.id,
          gesture: {
            key,
            gesture: {
              kind,
              surface: 'checklist',
              expected,
              swept:
                kind === 'tick'
                  ? sweptExpectation(row.id, plan)
                  : Object.fromEntries(
                      claimed
                        .filter((id) => id !== row.id)
                        .map((id) => [id, { delivered: false, closed: false }]),
                    ),
            },
          },
          ...(offline.surfaceKey ? { surfaceKey: offline.surfaceKey } : {}),
        },
        () => tickAction({ workItemId: row.id, kind, key, surface: 'checklist', deviceTime }),
      );
      if (!res.ok) {
        const settled = settleSweep(
          claimed,
          row.id,
          { ok: false, message: errorMessage(res.error) },
          kind === 'tick',
          actor,
        );
        setNotices((m) => {
          const n = new Map(m);
          for (const [id, message] of settled.notices) n.set(id, message);
          return n;
        });
        setNote(row.id, undefined);
        // A refused tick never stays in place; a refused UN-tick puts the atoms back
        // as the tap found them, so the line does not read open over work the
        // server still holds done. Either way it is the WHOLE claim that goes:
        // the plan was one statement and nothing was written.
        if (kind === 'tick') {
          editOwnJustTicked((m) => {
            for (const id of settled.retired) m.delete(id);
          });
        } else {
          for (const id of settled.retired) setKeptAtoms(id, before.get(id) ?? NO_ATOMS);
        }
        setPending({ ids: settled.retired, ticked: null });
        return;
      }
      if ('queued' in res) return; // the queue's overlay holds the reading
      if ('asked' in res) {
        // The tick became an ASK (§12 step 8): nothing changed on the item, so
        // the claim goes the way a refused one does - without a notice - and
        // the line says who was asked.
        setNote(row.id, undefined);
        if (kind === 'tick') {
          editOwnJustTicked((m) => {
            for (const id of claimed) m.delete(id);
          });
        }
        setPending({ ids: claimed, ticked: null });
        setAsked((m) => new Map(m).set(row.id, res.asked.answerers));
        return;
      }
      // The server's own answer replaces the optimistic one: the statement's
      // note, and a ROW PER ITEM the plan touched, patched in place of a whole
      // refresh. A claim the answer does not confirm - a tie the safe sweep
      // left where it is listed - is retired here, which is the one correction
      // this surface's own subtree can need.
      setNote(row.id, kind === 'tick' ? res.waits : undefined);
      const settled = settleSweep(
        claimed,
        row.id,
        { ok: true, rows: res.rows },
        kind === 'tick',
        actor,
      );
      setPatched((m) => {
        const n = new Map(m);
        for (const fresh of settled.took) n.set(fresh.id, fresh);
        return n;
      });
      // A tidy since the tap has folded this line already: the answer patches
      // the row, and the seat stays given up.
      if (kind === 'tick' && tidies.current === tidiesAtTap) {
        editOwnJustTicked((m) => {
          for (const fresh of settled.took) {
            const stood = m.get(fresh.id);
            // A row the plan closed that this surface never claimed (the server
            // swept deeper than the rail can see) is kept only where the rail
            // actually shows it; standing a line back in that was never there
            // would invent one.
            if (stood) {
              m.set(fresh.id, {
                ...stood,
                row: { ...stood.row, closed: fresh.closed, delivered: fresh.delivered },
              });
              continue;
            }
            const at = rows.findIndex((r) => r.id === fresh.id);
            if (at >= 0) m.set(fresh.id, { row: fresh, index: at });
          }
          for (const id of settled.retired) m.delete(id);
        });
      }
      if (settled.retired.length > 0) setPending({ ids: settled.retired, ticked: null });
    });
  }

  /**
   * **The lock as the entry** (docs/CHECKLIST.md §7, RESOLUTION.md §12 step 8):
   * a locked line's tap sends the tick's stimulus to its owners as an ask. The
   * line takes no reading - nothing about the item changes until an owner
   * accepts - only the quiet "asked <name>". A second tap returns the ask
   * already pending, so it cannot stack.
   */
  function ask(row: ListRow) {
    const key = mintKey(gestures.current++, Date.now(), Math.random());
    startAct(async () => {
      setNotices((m) => {
        if (!m.has(row.id)) return m;
        const n = new Map(m);
        n.delete(row.id);
        return n;
      });
      let res: Awaited<ReturnType<typeof proposeTickAction>>;
      try {
        res = await proposeTickAction({ workItemId: row.id, key });
      } catch {
        // An ask is not queued offline: it is a request to a person, and one
        // that silently waits in a phone is not an ask anyone received.
        res = {
          ok: false,
          error: domainError('invalid_state', m('lists.completion.unreachable')),
        };
      }
      if (!res.ok) {
        const message = errorMessage(res.error);
        setNotices((m) => new Map(m).set(row.id, message));
        return;
      }
      const answerers = res.asked.answerers;
      setAsked((m) => new Map(m).set(row.id, answerers));
    });
  }

  // The rail reads each notice as text, in the page's locale.
  const noticeText = useMemo(
    () =>
      notices.size === 0
        ? NO_NOTICE_TEXT
        : new Map([...notices].map(([id, message]) => [id, t.msg(message)])),
    [notices, t],
  );
  return {
    rows,
    completion: {
      pending,
      notes,
      notices: noticeText,
      asked,
      justTicked,
      standing,
      tidy,
      toggle,
      ask,
    },
  };
}

/**
 * The checklist's count, `done/total`, read the way the rail reads each line:
 * a locked line by its closed state, every other line by the viewer's own
 * in-flight tick, else by what the row's own atoms say. Rendered in the
 * surface's head beside the title (docs/CHECKLIST.md §3.1) - a count on its
 * own row above the lines read as an empty line (Leon, 2026-09-11).
 */
export function progressOf(
  rows: ListRow[],
  actor: Actor,
  completion: Completion,
): { done: number; total: number } {
  let done = 0;
  for (const row of rows) {
    const ticked = lockedFor(actor, row)
      ? row.closed
      : (completion.pending.get(row.id) ?? tickedOf(row, actor));
    if (ticked) done += 1;
  }
  return { done, total: rows.length };
}
